import React, { useState, useEffect } from "react";
import "./AdminDashboard.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const getAdminHeaders = () => {
  const token = localStorage.getItem("userAccessToken");
  const adminPassword =
    process.env.REACT_APP_ADMIN_PASSWORD || process.env.ADMIN_PASSWORD;
  return {
    ...(adminPassword ? { "X-Admin-Password": adminPassword } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// Trả về nhãn định dạng gói dịch vụ
const getMembershipPlanLabel = (membership, membershipExpiry) => {
  if (membership === undefined || membership === null) {
    return <span className="text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-xs">Free</span>;
  }
  const membershipInt = parseInt(membership, 10);
  const plans = {
    6: "Trải nghiệm",
    1: "Cơ bản",
    2: "Chuyên nghiệp",
    3: "Cao cấp",
    4: "VIP",
  };
  const name = plans[membershipInt] || `Plan ${membership}`;

  const formatDateLabel = (ts) => {
    if (!ts) return "";
    return new Date(ts).toLocaleDateString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" });
  };

  // Kiểm tra hết hạn
  const isExpired = membershipExpiry && Number(membershipExpiry) < Date.now();
  if (isExpired) {
    return (
      <span className="text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded text-xs font-medium" title={formatDateLabel(membershipExpiry)}>
        {name} (Hết hạn)
      </span>
    );
  }

  const colors = {
    6: "text-amber-700 bg-amber-50 border border-amber-200",
    1: "text-blue-700 bg-blue-50 border border-blue-200",
    2: "text-emerald-700 bg-emerald-50 border border-emerald-200",
    3: "text-indigo-700 bg-indigo-50 border border-indigo-200",
    4: "text-rose-700 bg-rose-50 border border-rose-200",
  };

  const className = colors[membershipInt] || "text-gray-700 bg-gray-50 border border-gray-200";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`} title={membershipExpiry ? `Hạn dùng: ${formatDateLabel(membershipExpiry)}` : undefined}>
      {name}
    </span>
  );
};

const AdminUserPostManagement = () => {
  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalPosts: 0,
    postsByType: {
      text: 0,
      image: 0,
      ai: 0,
    },
    postsByImageSource: {
      serper: 0,
      gemini: 0,
      upload: 0,
    },
    averageContentLength: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [filterBy, setFilterBy] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [users, setUsers] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchData();

    // Detect mobile view
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Hàm lấy dữ liệu từ Firebase
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersResponse, postsResponse] = await Promise.all([
        fetch(`${API_URL}/api/admin/users?limit=1000`, {
          headers: getAdminHeaders(),
        }),
        fetch(`${API_URL}/api/admin/user-posts?limit=1000`, {
          headers: getAdminHeaders(),
        }),
      ]);

      const [usersResult, postsResult] = await Promise.all([
        usersResponse.json(),
        postsResponse.json(),
      ]);

      if (!usersResponse.ok || !usersResult.success) {
        throw new Error(usersResult.message || "Không thể tải danh sách user");
      }

      if (!postsResponse.ok || !postsResult.success) {
        throw new Error(postsResult.message || "Không thể tải bài viết user");
      }

      setUsers(usersResult.data || []);
      setUserPosts(postsResult.data || []);
      const defaultStats = {
          totalPosts: 0,
          postsByType: { text: 0, image: 0, ai: 0 },
          postsByImageSource: { serper: 0, gemini: 0, upload: 0 },
          averageContentLength: 0,
        };
        const apiSummary = postsResult.summary || {};
        setStats({
          ...defaultStats,
          ...apiSummary,
          postsByType: { ...defaultStats.postsByType, ...(apiSummary.postsByType || {}) },
          postsByImageSource: { ...defaultStats.postsByImageSource, ...(apiSummary.postsByImageSource || {}) },
        });
    } catch (err) {
      console.error("Lỗi khi tải dữ liệu bài viết:", err);
      setError("Có lỗi khi tải dữ liệu bài viết. Vui lòng thử lại sau.");
    } finally {
      setLoading(false);
    }
  };

  // Tính toán thống kê
  const calculateStats = (posts) => {
    const stats = {
      totalPosts: posts.length,
      postsByType: {
        text: 0,
        image: 0,
        ai: 0,
      },
      postsByImageSource: {
        serper: 0,
        gemini: 0,
        upload: 0,
      },
      totalContentLength: 0,
      averageContentLength: 0,
    };

    posts.forEach((post) => {
      // Đếm theo loại bài viết
      if (post.postType) {
        stats.postsByType[post.postType] =
          (stats.postsByType[post.postType] || 0) + 1;
      }

      // Đếm theo nguồn ảnh
      if (post.imageSource) {
        stats.postsByImageSource[post.imageSource] =
          (stats.postsByImageSource[post.imageSource] || 0) + 1;
      }

      // Tính độ dài nội dung
      if (post.content) {
        stats.totalContentLength += post.content.length;
      }
    });

    // Tính trung bình độ dài nội dung
    if (posts.length > 0) {
      stats.averageContentLength = Math.round(
        stats.totalContentLength / posts.length
      );
    }

    setStats(stats);
  };

  // Hàm sắp xếp dữ liệu
  const sortPosts = (posts) => {
    return [...posts].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === "createdAt") {
        aValue = aValue || 0;
        bValue = bValue || 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Lọc bài viết theo điều kiện
  const getFilteredPosts = () => {
    const filtered = userPosts.filter((post) => {
      // Lọc theo loại bài viết
      if (filterBy !== "all" && post.postType !== filterBy) {
        return false;
      }

      // Lọc theo người dùng
      if (selectedUser && post.userId !== selectedUser) {
        return false;
      }

      // Lọc theo từ khóa tìm kiếm
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const contentMatch =
          post.content && post.content.toLowerCase().includes(searchLower);
        const titleMatch =
          post.title && post.title.toLowerCase().includes(searchLower);
        const promptMatch =
          post.prompt && post.prompt.toLowerCase().includes(searchLower);
        const userNameMatch =
          post.userName && post.userName.toLowerCase().includes(searchLower);

        return contentMatch || titleMatch || promptMatch || userNameMatch;
      }

      return true;
    });

    return sortPosts(filtered);
  };

  // Phân trang
  const filteredPosts = getFilteredPosts();
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  // Xử lý thay đổi trang
  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Định dạng thời gian
  const formatDate = (timestamp) => {
    if (!timestamp) return "Không xác định";
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Rút gọn nội dung để hiển thị
  const truncateContent = (content, maxLength = 150) => {
    if (!content) return "Không có nội dung";
    return content.length > maxLength
      ? content.substring(0, maxLength) + "..."
      : content;
  };

  // Hiển thị chi tiết bài viết
  const [selectedPost, setSelectedPost] = useState(null);
  const showPostDetails = async (post) => {
    setSelectedPost(post);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/user-posts/${post.userId}/${post.id}`,
        { headers: getAdminHeaders() }
      );
      const result = await response.json();
      if (response.ok && result.success) {
        setSelectedPost({
          ...post,
          ...result.data,
        });
      }
    } catch (error) {
      console.error("Lỗi khi tải chi tiết bài viết:", error);
    }
  };

  // Đóng modal chi tiết
  const closePostDetails = () => {
    setSelectedPost(null);
  };

  // Xử lý sắp xếp
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  // Export dữ liệu ra CSV
  const exportToCSV = () => {
    const headers = [
      "ID",
      "Người dùng",
      "Gói Plan",
      "Tiêu đề",
      "Nội dung",
      "Loại bài viết",
      "Nguồn ảnh",
      "Điểm sử dụng",
      "Ngày tạo",
    ];

    const csvContent = [
      headers.join(","),
      ...filteredPosts.map((post) =>
        [
          post.id,
          `"${post.userName || "Không xác định"}"`,
          `"${post.membership ? (post.membership === 6 ? "Trải nghiệm" : post.membership === 1 ? "Cơ bản" : post.membership === 2 ? "Chuyên nghiệp" : post.membership === 3 ? "Cao cấp" : post.membership === 4 ? "VIP" : `Plan ${post.membership}`) : "Free"}${post.membershipExpiry && Number(post.membershipExpiry) < Date.now() ? " (Hết hạn)" : ""}"`,
          `"${(post.title || "").replace(/"/g, '""')}"`,
          `"${(post.content || "").replace(/"/g, '""').replace(/\n/g, " ")}"`,
          post.postType === "text"
            ? "Văn bản"
            : post.postType === "image"
            ? "Ảnh"
            : "AI",
          post.imageSource === "serper"
            ? "Serper"
            : post.imageSource === "gemini"
            ? "Gemini"
            : post.imageSource === "upload"
            ? "Upload"
            : "N/A",
          post.pointsUsed || 0,
          formatDate(post.createdAt),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `danh-sach-bai-viet-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="content-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="mt-4 text-gray-600">Đang tải dữ liệu bài viết...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-container">
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Lỗi! </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="content-container">
        <div className="content-header flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-gray-800">
            Quản lý bài viết người dùng
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={exportToCSV}
              disabled={filteredPosts.length === 0}
              className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Export CSV
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Đang tải...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Làm mới
                </>
              )}
            </button>
          </div>
        </div>

        {/* Thống kê */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <p className="stat-card-title">Tổng số bài viết</p>
                <p className="stat-card-value">{stats.totalPosts}</p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-9 0V1m10 3V1m0 3l1 1v16a2 2 0 01-2 2H6a2 2 0 01-2-2V5l1-1z"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <p className="stat-card-title">Bài viết văn bản</p>
                <p className="stat-card-value">{stats.postsByType.text || 0}</p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <p className="stat-card-title">Bài viết từ ảnh</p>
                <p className="stat-card-value">
                  {stats.postsByType.image || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <p className="stat-card-title">Độ dài trung bình</p>
                <p className="stat-card-value">
                  {stats.averageContentLength} ký tự
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bộ lọc */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Loại bài viết
              </label>
              <select
                className="w-full border-gray-300 rounded-md shadow-sm p-2"
                value={filterBy}
                onChange={(e) => {
                  setFilterBy(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">Tất cả</option>
                <option value="text">Bài viết văn bản</option>
                <option value="image">Bài viết từ ảnh</option>
                <option value="ai">Bài viết AI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Người dùng
              </label>
              <select
                className="w-full border-gray-300 rounded-md shadow-sm p-2"
                value={selectedUser}
                onChange={(e) => {
                  setSelectedUser(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Tất cả người dùng</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName || user.email || user.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tìm kiếm
              </label>
              <input
                type="text"
                className="w-full border-gray-300 rounded-md shadow-sm p-2"
                placeholder="Tìm kiếm theo tiêu đề, nội dung hoặc tên người dùng"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
              onClick={() => {
                setFilterBy("all");
                setSelectedUser("");
                setSearchTerm("");
                setCurrentPage(1);
              }}
            >
              Đặt lại bộ lọc
            </button>
          </div>
        </div>

        {/* Danh sách bài viết */}
        {isMobile ? (
          <div className="posts-mobile-cards">
            {currentPosts.length > 0 ? (
              currentPosts.map((post) => (
                <div key={post.id} className="post-mobile-card">
                  <div className="post-mobile-header flex justify-between items-center">
                    <div className="flex flex-col">
                      <div className="post-mobile-user font-semibold text-gray-800">
                        {post.userName || "Không xác định"}
                      </div>
                      <div className="mt-1">
                        {getMembershipPlanLabel(post.membership, post.membershipExpiry)}
                      </div>
                    </div>
                    <span
                      className={`post-mobile-type ${post.postType || "ai"}`}
                    >
                      {post.postType === "text"
                        ? "Văn bản"
                        : post.postType === "image"
                        ? "Ảnh"
                        : "AI"}
                    </span>
                  </div>

                  <div className="post-mobile-content">
                    {post.title && (
                      <div className="post-mobile-title">
                        {truncateContent(post.title, 50)}
                      </div>
                    )}
                    <div className="post-mobile-text">
                      {truncateContent(post.content, post.title ? 80 : 120)}
                    </div>
                  </div>

                  <div className="post-mobile-meta">
                    <span className="post-mobile-date">
                      {formatDate(post.createdAt)}
                    </span>
                    <div className="post-mobile-actions">
                      <button
                        className="post-mobile-btn primary"
                        onClick={() => showPostDetails(post)}
                      >
                        Chi tiết
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                Không tìm thấy bài viết nào
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("userName")}
                    >
                      <div className="flex items-center gap-1">
                        Người dùng
                        {sortBy === "userName" && (
                          <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("membership")}
                    >
                      <div className="flex items-center gap-1">
                        Gói Plan
                        {sortBy === "membership" && (
                          <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tiêu đề/Nội dung
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("postType")}
                    >
                      <div className="flex items-center gap-1">
                        Loại
                        {sortBy === "postType" && (
                          <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nguồn ảnh
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("createdAt")}
                    >
                      <div className="flex items-center gap-1">
                        Ngày tạo
                        {sortBy === "createdAt" && (
                          <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentPosts.length > 0 ? (
                    currentPosts.map((post) => (
                      <tr key={post.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {post.id.substring(0, 8)}...
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {post.userName || "Không xác định"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getMembershipPlanLabel(post.membership, post.membershipExpiry)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {post.title ? (
                            <div>
                              <div className="font-medium text-gray-900">
                                {truncateContent(post.title, 50)}
                              </div>
                              <div className="text-gray-500">
                                {truncateContent(post.content, 100)}
                              </div>
                            </div>
                          ) : (
                            <div>{truncateContent(post.content, 150)}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              post.postType === "text"
                                ? "bg-green-100 text-green-800"
                                : post.postType === "image"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {post.postType === "text"
                              ? "Văn bản"
                              : post.postType === "image"
                              ? "Ảnh"
                              : "AI"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {post.imageSource === "serper"
                            ? "Serper"
                            : post.imageSource === "gemini"
                            ? "Gemini"
                            : post.imageSource === "upload"
                            ? "Upload"
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(post.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            className="text-indigo-600 hover:text-indigo-900"
                            onClick={() => showPostDetails(post)}
                          >
                            Xem chi tiết
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-6 py-4 text-center text-sm text-gray-500"
                      >
                        Không tìm thấy bài viết nào
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Phân trang */}
        {filteredPosts.length > postsPerPage && (
          <div className="bg-white px-4 py-3 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700 text-center sm:text-left">
                Hiển thị{" "}
                <span className="font-medium">{indexOfFirstPost + 1}</span> đến{" "}
                <span className="font-medium">
                  {Math.min(indexOfLastPost, filteredPosts.length)}
                </span>{" "}
                trong tổng số{" "}
                <span className="font-medium">{filteredPosts.length}</span> bài
                viết
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 text-sm font-medium rounded-md border ${
                    currentPage === 1
                      ? "text-gray-300 cursor-not-allowed border-gray-200 bg-gray-50"
                      : "text-gray-500 hover:bg-gray-50 border-gray-300 bg-white"
                  }`}
                >
                  ‹ Trước
                </button>

                {/* Mobile: Show fewer pages */}
                {isMobile ? (
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-700">
                      {currentPage} / {totalPages}
                    </span>
                  </div>
                ) : (
                  <nav
                    className="flex items-center gap-1"
                    aria-label="Pagination"
                  >
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md border ${
                            currentPage === pageNum
                              ? "bg-blue-50 border-blue-500 text-blue-600"
                              : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {totalPages > 5 && (
                      <>
                        {currentPage < totalPages - 2 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        {currentPage < totalPages - 2 && (
                          <button
                            onClick={() => paginate(totalPages)}
                            className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                          >
                            {totalPages}
                          </button>
                        )}
                      </>
                    )}
                  </nav>
                )}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 text-sm font-medium rounded-md border ${
                    currentPage === totalPages
                      ? "text-gray-300 cursor-not-allowed border-gray-200 bg-gray-50"
                      : "text-gray-500 hover:bg-gray-50 border-gray-300 bg-white"
                  }`}
                >
                  Sau ›
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal chi tiết bài viết */}
        {selectedPost && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity"
                aria-hidden="true"
                onClick={closePostDetails}
              >
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full w-11/12 max-w-4xl mx-4">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[90vh] overflow-hidden flex flex-col">
                  <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h3
                      className="text-lg leading-6 font-medium text-gray-900"
                      id="modal-headline"
                    >
                      Chi tiết bài viết
                    </h3>
                    <button
                      onClick={closePostDetails}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                      aria-label="Đóng"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            ID Bài viết
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedPost.id}
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Người dùng
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedPost.userName} ({selectedPost.userId})
                          </p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Gói dịch vụ
                          </h4>
                          <div className="mt-1">
                            {getMembershipPlanLabel(selectedPost.membership, selectedPost.membershipExpiry)}
                          </div>
                        </div>
                      </div>

                      {selectedPost.title && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Tiêu đề
                          </h4>
                          <p className="mt-1 text-sm text-gray-900 font-medium">
                            {selectedPost.title}
                          </p>
                        </div>
                      )}

                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Nội dung
                        </h4>
                        <div className="mt-1 text-sm text-gray-900 whitespace-pre-line border p-3 rounded max-h-60 overflow-y-auto bg-gray-50">
                          {selectedPost.content}
                        </div>
                      </div>

                      {selectedPost.prompt && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Yêu cầu ban đầu
                          </h4>
                          <p className="mt-1 text-sm text-gray-900 italic bg-gray-50 p-2 rounded">
                            {selectedPost.prompt}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Loại bài viết
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedPost.postType === "text"
                              ? "Văn bản"
                              : selectedPost.postType === "image"
                              ? "Từ hình ảnh"
                              : "AI"}
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Nguồn ảnh
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedPost.imageSource === "serper"
                              ? "Serper"
                              : selectedPost.imageSource === "gemini"
                              ? "Gemini"
                              : selectedPost.imageSource === "upload"
                              ? "Upload"
                              : "Không có"}
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Độ dài
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedPost.postLength || "Không xác định"}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Điểm sử dụng
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">
                            {selectedPost.pointsUsed || 0}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Phong cách
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">
                            {Array.isArray(selectedPost.postStyles)
                              ? selectedPost.postStyles.join(", ")
                              : selectedPost.postStyles || "Không xác định"}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Ngày tạo
                          </h4>
                          <p className="mt-1 text-sm text-gray-900">
                            {formatDate(selectedPost.createdAt)}
                          </p>
                        </div>
                      </div>

                      {selectedPost.image && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">
                            Hình ảnh
                          </h4>
                          <div className="mt-2">
                            <img
                              src={selectedPost.image}
                              alt="Hình ảnh bài viết"
                              className="max-h-60 rounded border mx-auto"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "https://via.placeholder.com/400x300?text=Không+tải+được+ảnh";
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {selectedPost.images &&
                        selectedPost.images.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-500">
                              Hình ảnh
                            </h4>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              {selectedPost.images.map((img, index) => (
                                <img
                                  key={index}
                                  src={img}
                                  alt={`Hình ảnh ${index + 1}`}
                                  className="max-h-40 rounded border mx-auto"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src =
                                      "https://via.placeholder.com/200x150?text=Không+tải+được+ảnh";
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 border-t flex justify-end">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={closePostDetails}
                  >
                    Đóng
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal chi tiết bài viết */}
      {selectedPost && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
              onClick={closePostDetails}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full w-11/12 max-w-4xl mx-4">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                  <h3
                    className="text-lg leading-6 font-medium text-gray-900"
                    id="modal-headline"
                  >
                    Chi tiết bài viết
                  </h3>
                  <button
                    onClick={closePostDetails}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                    aria-label="Đóng"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          ID Bài viết
                        </h4>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedPost.id}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Người dùng
                        </h4>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedPost.userName} ({selectedPost.userId})
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Gói dịch vụ
                        </h4>
                        <div className="mt-1">
                          {getMembershipPlanLabel(selectedPost.membership, selectedPost.membershipExpiry)}
                        </div>
                      </div>
                    </div>

                    {selectedPost.title && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Tiêu đề
                        </h4>
                        <p className="mt-1 text-sm text-gray-900 font-medium">
                          {selectedPost.title}
                        </p>
                      </div>
                    )}

                    <div>
                      <h4 className="text-sm font-medium text-gray-500">
                        Nội dung
                      </h4>
                      <div className="mt-1 text-sm text-gray-900 whitespace-pre-line border p-3 rounded max-h-60 overflow-y-auto bg-gray-50">
                        {selectedPost.content}
                      </div>
                    </div>

                    {selectedPost.prompt && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Yêu cầu ban đầu
                        </h4>
                        <p className="mt-1 text-sm text-gray-900 italic bg-gray-50 p-2 rounded">
                          {selectedPost.prompt}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Loại bài viết
                        </h4>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedPost.postType === "text"
                            ? "Văn bản"
                            : selectedPost.postType === "image"
                            ? "Từ hình ảnh"
                            : "AI"}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Nguồn ảnh
                        </h4>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedPost.imageSource === "serper"
                            ? "Serper"
                            : selectedPost.imageSource === "gemini"
                            ? "Gemini"
                            : selectedPost.imageSource === "upload"
                            ? "Upload"
                            : "Không có"}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Độ dài
                        </h4>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedPost.postLength || "Không xác định"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Điểm sử dụng
                        </h4>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedPost.pointsUsed || 0}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Phong cách
                        </h4>
                        <p className="mt-1 text-sm text-gray-900">
                          {Array.isArray(selectedPost.postStyles)
                            ? selectedPost.postStyles.join(", ")
                            : selectedPost.postStyles || "Không xác định"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Ngày tạo
                        </h4>
                        <p className="mt-1 text-sm text-gray-900">
                          {formatDate(selectedPost.createdAt)}
                        </p>
                      </div>
                    </div>

                    {selectedPost.image && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Hình ảnh
                        </h4>
                        <div className="mt-2">
                          <img
                            src={selectedPost.image}
                            alt="Hình ảnh bài viết"
                            className="max-h-60 rounded border mx-auto"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://via.placeholder.com/400x300?text=Không+tải+được+ảnh";
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {selectedPost.images && selectedPost.images.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">
                          Hình ảnh
                        </h4>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {selectedPost.images.map((img, index) => (
                            <img
                              key={index}
                              src={img}
                              alt={`Hình ảnh ${index + 1}`}
                              className="max-h-40 rounded border mx-auto"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "https://via.placeholder.com/200x150?text=Không+tải+được+ảnh";
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 border-t flex justify-end">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={closePostDetails}
                  >
                    Đóng
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUserPostManagement;
